/**
 * Anonymous engagement transport (plan Flow B "Transport behavior"): a
 * bounded in-memory queue of strict event bodies, batched to at most 20
 * events per POST to the brand auth host with `credentials: 'include'`.
 * Retries reuse the same producer event ids so the server deduplicates
 * end-to-end; 429/5xx/network retry with bounded jitter, schema/consent
 * failures do not. The JSON body goes as text/plain: a CORS simple request,
 * so no preflight (2026-10-05; the edge guard checks Origin and Fetch
 * Metadata). Unload delivery uses `fetch` with `keepalive`, whose response
 * stays readable, rather than `sendBeacon`. No localStorage backlog; the page
 * never blocks on delivery.
 * @module lib/identity/transport
 */

const { newProducerEventId } = require('./ids');

const MAX_BATCH_EVENTS = 20;
const MAX_QUEUE_EVENTS = 200;
const MAX_ATTEMPTS = 3;
const DEFAULT_FLUSH_DELAY_MS = 1500;
const RETRY_BASE_MS = 2000;
const REQUEST_TIMEOUT_MS = 6000;

function jitteredBackoff(attempt) {
  const base = RETRY_BASE_MS * (2 ** Math.max(0, attempt - 1));
  return Math.floor(base * (0.8 + Math.random() * 0.4));
}

function createTransport({ endpoint, producerVersion, sessionId, fetchImpl, flushDelayMs = DEFAULT_FLUSH_DELAY_MS, onDisabled, onAccepted, log } = {}) {
  const queue = [];
  let flushTimer = null;
  let inFlight = false;
  let enabled = true;
  let retryAttempt = 0;
  let unloadHooked = false;
  const stats = { queued: 0, sent: 0, persisted: 0, duplicates: 0, rejected: 0, quarantined: 0, failed: 0, dropped: 0, batches: 0 };

  const resolveFetch = () => fetchImpl || (typeof fetch === 'function' ? fetch : null);

  function schedule(delay = flushDelayMs) {
    if (flushTimer || !enabled) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flush().catch(() => {});
    }, delay);
    // Node test runs: never keep the process alive for a pending flush.
    if (flushTimer && typeof flushTimer.unref === 'function') flushTimer.unref();
  }

  function enqueue(event) {
    if (!enabled) {
      stats.dropped += 1;
      return null;
    }
    if (queue.length >= MAX_QUEUE_EVENTS) {
      queue.shift();
      stats.dropped += 1;
    }
    const entry = {
      producerEventId: event.producerEventId || newProducerEventId(),
      sourceEventType: event.sourceEventType,
      occurredAt: event.occurredAt || new Date().toISOString(),
      object: event.object,
      context: event.context || {}
    };
    queue.push(entry);
    stats.queued += 1;
    schedule();
    return entry.producerEventId;
  }

  function disable(reason) {
    enabled = false;
    queue.length = 0;
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (typeof onDisabled === 'function') onDisabled(reason);
  }

  async function send(events, { keepalive = false } = {}) {
    const doFetch = resolveFetch();
    if (!doFetch) throw new Error('fetch_unavailable');
    const body = JSON.stringify({
      schemaVersion: 1,
      producer: 'publisher_sdk',
      producerVersion,
      sessionId,
      sentAt: new Date().toISOString(),
      events
    });
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS) : null;
    try {
      return await doFetch(endpoint(), {
        method: 'POST',
        credentials: 'include',
        cache: 'no-store',
        keepalive,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body,
        signal: controller ? controller.signal : undefined
      });
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  function requeue(events) {
    // Same producer event ids; front of the queue so order is kept.
    for (let index = events.length - 1; index >= 0; index -= 1) queue.unshift(events[index]);
  }

  async function flush({ unload = false } = {}) {
    if (!enabled || inFlight || queue.length === 0) return null;
    inFlight = true;
    const events = queue.splice(0, MAX_BATCH_EVENTS);
    stats.batches += 1;
    try {
      const response = await send(events, { keepalive: unload });
      const data = await response.json().catch(() => ({}));
      if (response.status === 200) {
        stats.sent += events.length;
        stats.persisted += Number(data.persisted) || 0;
        stats.duplicates += Number(data.duplicates) || 0;
        stats.rejected += Number(data.rejected) || 0;
        stats.quarantined += Number(data.quarantined) || 0;
        retryAttempt = 0;
        // The auth host kept at least one event under this page's credential
        // (queued for intake, or persisted/deduplicated inline): the browser
        // presents the cookie it was issued.
        if (onAccepted && (data.queued > 0 || data.persisted > 0 || data.duplicates > 0)) {
          try { onAccepted(); } catch (_) { /* a listener never breaks delivery */ }
        }
      } else if (response.status === 401) {
        // No durable visitor on this host: stop until the next bootstrap.
        stats.dropped += events.length;
        disable('no_visitor_credential');
      } else if (response.status === 503 && data && data.reason === 'CAPTURE_DISABLED') {
        stats.dropped += events.length;
        disable('capture_disabled');
      } else if (response.status === 429 || response.status >= 500) {
        retryAttempt += 1;
        if (retryAttempt > MAX_ATTEMPTS) {
          stats.failed += events.length;
          retryAttempt = 0;
        } else {
          requeue(events);
          const retryAfter = Number(response.headers && typeof response.headers.get === 'function' ? response.headers.get('Retry-After') : 0);
          inFlight = false;
          schedule(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : jitteredBackoff(retryAttempt));
          return { retried: true };
        }
      } else {
        // 400/403: permanent for this batch (schema, consent, origin).
        stats.rejected += events.length;
        if (log) log('warn', 'engagement batch rejected', { status: response.status, reason: data && data.reason });
      }
    } catch (error) {
      // Network ambiguity: retry-safe duplicate delivery with the same ids.
      retryAttempt += 1;
      if (retryAttempt > MAX_ATTEMPTS || unload) {
        stats.failed += events.length;
        retryAttempt = 0;
      } else {
        requeue(events);
        inFlight = false;
        schedule(jitteredBackoff(retryAttempt));
        return { retried: true };
      }
    } finally {
      inFlight = false;
    }
    if (queue.length) schedule(unload ? 0 : flushDelayMs);
    return { flushed: events.length };
  }

  function hookUnload() {
    if (unloadHooked || typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    unloadHooked = true;
    const onHide = () => {
      if (typeof document !== 'undefined' && document.visibilityState && document.visibilityState !== 'hidden') return;
      if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      flush({ unload: true }).catch(() => {});
    };
    window.addEventListener('pagehide', onHide);
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', onHide);
    }
  }

  return {
    enqueue,
    flush,
    disable,
    hookUnload,
    isEnabled: () => enabled,
    size: () => queue.length,
    stats: () => ({ ...stats })
  };
}

module.exports = {
  MAX_ATTEMPTS,
  MAX_BATCH_EVENTS,
  MAX_QUEUE_EVENTS,
  createTransport,
  jitteredBackoff
};
